"use client";

import React from "react";
import { ChartBar as BarChart3, Compass, Eye, MapPin, PhoneCall, Send, Target, TrendingUp, ArrowRight, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const measurementMetrics = [{
  id: 1,
  title: "Organic visibility",
  category: "Search Footprint",
  description: "I monitor total impressions and search coverage across Google to ensure your brand surfaces consistently for relevant search journeys.",
  icon: "Eye"
}, {
  id: 2,
  title: "Local keyword rankings",
  category: "Regional Focus",
  description: "I evaluate your positions across targeted towns and counties for high-intent searches that bring customers through your doors.",
  icon: "MapPin"
}, {
  id: 3,
  title: "Google Maps & Map Pack",
  category: "Local Pack",
  description: "I track your Google Business Profile visibility in the top 3-pack, measuring direct direction requests, clicks, and discovery calls.",
  icon: "Compass"
}, {
  id: 4,
  title: "Phone calls & direct contact",
  category: "Direct Inbound",
  description: "I set up call-tracking and click-to-call monitoring to measure genuine telephone enquiries generated by organic searchers.",
  icon: "PhoneCall"
}, {
  id: 5,
  title: "Inbound form enquiries",
  category: "Lead Generation",
  description: "I evaluate form fills, quote requests, and consultation bookings directly attributed to organic landing pages.",
  icon: "Send"
}, {
  id: 6,
  title: "Organic traffic quality",
  category: "Audience Intent",
  description: "I look beyond raw visitor volume, focusing on engaged sessions, bounce rates, and visitors who match your ideal client profile.",
  icon: "TrendingUp"
}, {
  id: 7,
  title: "Conversions & commercial value",
  category: "Business Impact",
  description: "I track completed orders, signed retainers, and macro conversions to confirm that organic growth supports your bottom line.",
  icon: "BarChart3"
}, {
  id: 8,
  title: "Commercial keyword rankings",
  category: "Purchase Intent",
  description: "I prioritise top rankings for buyer-intent search phrases rather than vanity search terms with low commercial value.",
  icon: "Target"
}];
const renderMetricIcon = (iconName: string) => {
  const iconProps = {
    className: "size-5 text-primary"
  };
  switch (iconName) {
    case "Eye":
      return <Eye {...iconProps} />;
    case "MapPin":
      return <MapPin {...iconProps} />;
    case "Compass":
      return <Compass {...iconProps} />;
    case "PhoneCall":
      return <PhoneCall {...iconProps} />;
    case "Send":
      return <Send {...iconProps} />;
    case "TrendingUp":
      return <TrendingUp {...iconProps} />;
    case "BarChart3":
      return <BarChart3 {...iconProps} />;
    case "Target":
      return <Target {...iconProps} />;
    default:
      return <BarChart3 {...iconProps} />;
  }
};
export default function SeoResults() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="seoresultsexperience" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-primary bg-secondary border-none">
            <ShieldCheck className="size-3.5 text-primary" />
            Transparent SEO Measurement
          </Badge>

          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl font-default">
            How I measure SEO performance
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            I do not make unverified ranking promises or rely on superficial vanity numbers. Instead, I track meaningful commercial metrics that reflect genuine business enquiries, telephone calls, and revenue growth.
          </p>
        </div>

        {/* 8 Metric Dimensions Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {measurementMetrics.map((metric, i) => <motion.div key={metric.id} data-index={i} variants={itemVariants} whileHover={shouldReduceMotion ? {} : {
          y: -4
        }} className="group flex flex-col justify-between rounded-xl border border-border bg-card p-6 shadow-xs transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-md">
              <div>
                <div className="flex items-center justify-between gap-3 mb-5">
                  <div className="flex size-11 items-center justify-center rounded-lg bg-primary/10 border border-primary/20 group-hover:bg-primary/15 transition-colors">
                    {renderMetricIcon(metric.icon)}
                  </div>
                  <span className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
                    {metric.category}
                  </span>
                </div>

                <h3 className="text-lg font-semibold text-foreground font-default mb-2">
                  {metric.title}
                </h3>

                <p className="text-sm text-muted-foreground font-default leading-relaxed">
                  {metric.description}
                </p>
              </div>

              <div className="mt-6 pt-4 border-t border-border/60 flex items-center gap-2 text-xs font-medium text-primary">
                <span>Tracked in client reporting</span>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Value Proposition Callout & CTA */}
        <div className="mt-16 md:mt-20 rounded-2xl border border-border bg-card p-8 md:p-12 text-center shadow-xs">
          <div className="mx-auto max-w-2xl">
            <h3 className="text-2xl font-bold text-foreground font-default sm:text-3xl">
              Direct access and honest reporting
            </h3>
            <p className="mt-3 text-muted-foreground text-sm sm:text-base font-default leading-relaxed">
              When you collaborate with me, you work directly with an independent specialist. I provide clear monthly reviews detailing exact lead figures, keyword positions, and technical fixes.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto font-medium shadow-sm">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium">
                <Link to="/CaseStudiesPage">
                  View Verified Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}